import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation, NavLink } from "react-router-dom";
import {
  Bot, LayoutDashboard, BookOpen, Video, HelpCircle, Download, Sparkles, PhoneCall,
  Search, ChevronsLeft, ChevronsRight, LogOut, RefreshCw, Menu, Command, Globe2, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PROGRAM_NAV = [
  { name: "Home",             icon: LayoutDashboard, path: "dashboard" },
  { name: "AI Assistant",     icon: Bot,             path: "chat" },
  { name: "SOP Library",      icon: BookOpen,        path: "sops" },
  { name: "Training Videos",  icon: Video,           path: "training" },
  { name: "FAQs & Workflows", icon: HelpCircle,      path: "faqs" },
  { name: "Downloads",        icon: Download,        path: "downloads" },
];

const GLOBAL_NAV = [
  { name: "What's New", icon: Sparkles,  path: "whats-new" },
  { name: "Support",    icon: PhoneCall, path: "support" },
];

export default function AppShell() {
  const { user, logout, activeProgram, setActiveProgram } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [programs, setPrograms] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const isManager = user?.is_admin || user?.role_category === "rc_approver";

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (!activeProgram) navigate("/"); }, [activeProgram, navigate]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const program = programs.find((p) => p.id === activeProgram);
  const currentName = [...PROGRAM_NAV, ...GLOBAL_NAV].find((n) => location.pathname.endsWith(n.path))?.name || "Workspace";
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
  const multiProgram = programs.length > 1;

  const NavGroup = ({ label, items }) => (
    <div>
      {!collapsed && (
        <p className="px-3 mb-2 mt-1 text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 font-semibold font-mono">
          {label}
        </p>
      )}
      <div className="space-y-0.5">
        {items.map((item) => (
          <NavLink key={item.path} to={item.path} data-testid={`nav-${item.path}`}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-[0_6px_18px_-8px_hsl(224_54%_21%/0.55)]"
                  : "text-foreground/70 hover:bg-secondary/60 hover:text-foreground"
              } ${collapsed ? "justify-center px-2" : ""}`
            }
            title={item.name}>
            <item.icon className="w-4 h-4 shrink-0" />
            {!collapsed && <span className="truncate flex-1">{item.name}</span>}
          </NavLink>
        ))}
      </div>
    </div>
  );

  const SidebarInner = () => (
    <>
      <div className={`px-4 py-5 border-b border-border ${collapsed ? "px-2" : ""}`}>
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shrink-0 shadow-[0_8px_20px_-8px_hsl(224_54%_21%/0.5)]">
            <Bot className="w-5 h-5 text-primary-foreground" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="font-display text-[17px] leading-tight text-foreground truncate">Ask <span className="italic text-primary">Z-Method</span></p>
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">Process Portal</p>
            </div>
          )}
        </div>
        {!collapsed && program && (
          <div className="mt-4 rounded-xl border border-border bg-secondary/40 px-3 py-2.5">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Active program</p>
            <p className="text-sm font-semibold text-foreground truncate mt-0.5">{program.name}</p>
            <div className="flex items-center justify-between mt-0.5">
              <p className="text-[11px] font-mono text-primary">{program.jira_tag}</p>
              {multiProgram && (
                <button onClick={() => { setActiveProgram(null); navigate("/"); }} data-testid="sidebar-switch-program"
                  className="text-[11px] text-primary hover:underline flex items-center gap-1 font-medium">
                  <RefreshCw className="w-3 h-3" /> Switch
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto kp-scroll py-4 px-2.5 space-y-5">
        <NavGroup label={program ? "This program" : "Program"} items={PROGRAM_NAV} />
        <NavGroup label="All programs" items={GLOBAL_NAV} />
      </nav>

      {isManager && (
        <button onClick={() => navigate("/manage/overview")} data-testid="go-management-console"
          className={`m-2.5 rounded-xl border border-primary/20 bg-primary/5 text-left px-3 py-2.5 hover:bg-primary/10 transition-all group ${collapsed ? "px-2 text-center" : ""}`}>
          {collapsed ? <Command className="w-4 h-4 text-primary mx-auto" /> : (
            <>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Switch to</p>
              <p className="text-sm font-semibold text-primary flex items-center gap-1.5 mt-0.5">
                <Command className="w-3.5 h-3.5" /> Management Console
                <ArrowRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
              </p>
            </>
          )}
        </button>
      )}

      <button onClick={() => setCollapsed((c) => !c)}
        className="hidden lg:flex items-center gap-2 px-4 py-3 border-t border-border text-[11px] font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
        {collapsed ? <ChevronsRight className="w-4 h-4" /> : <><ChevronsLeft className="w-4 h-4" /> Collapse</>}
      </button>
    </>
  );

  return (
    <div className="min-h-screen kp-paper flex">
      <aside className={`hidden lg:flex flex-col bg-card/60 backdrop-blur border-r border-border transition-all duration-300 ${collapsed ? "w-16" : "w-64"}`}>
        <SidebarInner />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden kp-fade">
          <div className="absolute inset-0 bg-foreground/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-card flex flex-col shadow-2xl kp-in"><SidebarInner /></aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-background/85 backdrop-blur-md border-b border-border flex items-center gap-3 px-5 sticky top-0 z-30">
          <button className="lg:hidden text-foreground" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          <div className="hidden md:flex items-center text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground gap-1.5 min-w-0">
            <Globe2 className="w-3 h-3" />
            <span className="truncate">{program?.name || "Workspace"}</span>
            <span className="opacity-40">/</span>
            <span className="text-foreground font-semibold truncate">{currentName}</span>
          </div>

          <button onClick={() => setSearchOpen(true)} data-testid="global-search-trigger"
            className="ml-auto md:ml-6 flex-1 min-w-0 max-w-md flex items-center gap-2 rounded-full border border-border bg-card/70 px-4 py-2 text-sm text-muted-foreground hover:border-primary/40 hover:bg-card transition-all">
            <Search className="w-4 h-4 shrink-0" />
            <span className="truncate min-w-0 font-normal">Ask anything — "how do I print a Zap91 label?"</span>
            <kbd className="ml-auto hidden sm:inline text-[10px] font-mono bg-background border border-border rounded px-1.5 py-0.5">⌘K</kbd>
          </button>

          {program && multiProgram && (
            <div className="hidden xl:flex items-center gap-2 rounded-full bg-secondary/60 border border-secondary pl-3 pr-1 py-1">
              <span className="text-xs font-medium text-primary">{program.name}</span>
              <button onClick={() => { setActiveProgram(null); navigate("/"); }} data-testid="topbar-switch-program"
                className="text-[11px] rounded-full bg-card border border-primary/20 px-2 py-0.5 text-primary hover:bg-primary hover:text-primary-foreground transition">
                Switch
              </button>
            </div>
          )}

          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full hover:bg-secondary/60 pl-1 pr-2 py-1 transition" data-testid="user-menu-trigger">
                <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center uppercase">{initials}</span>
                <span className="hidden sm:block text-left leading-tight">
                  <span className="block text-xs font-semibold text-foreground">{user?.name}</span>
                  <span className="block text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{user?.is_admin ? "Administrator" : user?.department}</span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="font-semibold">{user?.name}</p>
                <p className="text-xs text-muted-foreground font-normal">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="px-2 py-1 text-xs text-muted-foreground space-y-1">
                <div className="flex justify-between"><span>Role</span><span className="font-medium text-foreground capitalize">{user?.role_category?.replace("rc_", "")}</span></div>
                <div className="flex justify-between"><span>Country</span><span className="font-medium text-foreground">{user?.country}</span></div>
              </div>
              <DropdownMenuSeparator />
              {isManager && (
                <DropdownMenuItem onClick={() => navigate("/manage/overview")} data-testid="menu-management-console">
                  <Command className="w-4 h-4 mr-2" /> Management Console
                </DropdownMenuItem>
              )}
              {multiProgram && (
                <DropdownMenuItem onClick={() => { setActiveProgram(null); navigate("/"); }} data-testid="menu-switch-program">
                  <RefreshCw className="w-4 h-4 mr-2" /> Switch Program
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
                <LogOut className="w-4 h-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto kp-scroll p-5 md:p-10 max-w-[1360px] w-full">
          <Outlet />
        </main>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} activeProgram={activeProgram} />
    </div>
  );
}
